import _ from "lodash";
import * as React from "react";
import styled, { css } from "styled-components";

import Grid from "src/library/components/Hedron";
import { Text } from "src/library/components/Text";
import { breakpoint } from "src/library/utils/styled-media-queries";
import {
  OrderLinesGroupedByRelatedGroupParent,
  getOrderLinesByRelatedGroup
} from "src/core/orders/utils/orders";
import { IProductRelatedGroup } from "src/core/products/api/products.api";
import { Link } from "src/library/components/Link";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { IListLine } from "src/core/lists/api/lists.api";

import { IOrderLine } from "../api/orders.api";

type OrderOrListLines = IOrderLine[] | IListLine[];
interface IOrderLeadTimeSplitProps {
  orderLines: OrderOrListLines;
  OrderProductLine: (orderLine: IOrderLine | IListLine) => React.ReactNode;
  productRelatedGroups: IProductRelatedGroup[];
}

const RelatedProductSplit: React.ReactFCWithChildren<IOrderLeadTimeSplitProps> = ({
  OrderProductLine, orderLines, productRelatedGroups
}) => {
  const [ genericOrderLines, setGenericOrderLines ] = React.useState<OrderOrListLines>([]);
  const [ orderLinesByRelatedGroup, setOrderLinesByRelatedGroup ] = React.useState<OrderLinesGroupedByRelatedGroupParent>({});

  React.useEffect(() => {
    const {
      orderLinesGroupedByParent, childrenWithNoParent, genericOrderLines
    } = getOrderLinesByRelatedGroup(orderLines, productRelatedGroups);

    setGenericOrderLines(genericOrderLines || []);

    if (childrenWithNoParent?.length) {
      // generate grouped by parent for children
      const groupedChildrenByParent: OrderLinesGroupedByRelatedGroupParent = _.reduce(childrenWithNoParent, (prev, ol) => {
        const parentGroupId = _.find(productRelatedGroups, prg => prg.id === ol.product.relatedGroupId)?.parentId || "none";

        if (prev[ parentGroupId ]?.childOrderLines) {
          prev[ parentGroupId ].childOrderLines.push(ol);
        } else {
          prev[ parentGroupId ] = {
            childOrderLines: [ ol ],
            parentOrderLines: []
          };
        }

        return prev;
      }, {});

      setOrderLinesByRelatedGroup({
        ...orderLinesGroupedByParent,
        ...groupedChildrenByParent
      });
    } else {
      setOrderLinesByRelatedGroup(orderLinesGroupedByParent);
    }
  }, [ orderLines, productRelatedGroups ]);

  if (orderLines.length) {
    return (
      <RelatedGroupsWrapper>
        {_.keys(orderLinesByRelatedGroup)
          .map(key => {
            if (orderLinesByRelatedGroup[ key ]) {
              const childRelatedGroupId = orderLinesByRelatedGroup[ key ]?.childOrderLines[ 0 ]?.product.relatedGroupId || null;
              const childRelatedGroup = productRelatedGroups.find(group => group.parentId?.toString() === key);
              const parentRelatedGroup = productRelatedGroups.find(group => group.id.toString() === key);
              const parentTotal = orderLinesByRelatedGroup[ key ]?.parentOrderLines.reduce((acc, ol) => acc + ol.quantity, 0);
              const childTotal = orderLinesByRelatedGroup[ key ]?.childOrderLines.reduce((acc, ol) => acc + ol.quantity, 0);

              return (
                <div key={key}>
                  {!!orderLinesByRelatedGroup[ key ].parentOrderLines.length && (
                    <RelatedGroupSection
                      direction="vertical"
                    >
                      <div className="parent-order-lines">
                        <Grid.Box className="related-group-header">
                          <Text color="white">
                            {productRelatedGroups.find(group => group.id.toString() === key)?.terminology.plural || "Related Product Group"}
                          </Text>

                          {childRelatedGroup?.categoryId && (
                            <Link to={`/products?category=${childRelatedGroup?.categoryId}`}>
                              {`View ${childRelatedGroup?.terminology.plural}`}
                            </Link>
                          )}
                        </Grid.Box>

                        {childRelatedGroup?.settings?.parentProducts?.errorLevel && parentTotal > childTotal && (
                          <ErrorNotice
                            type={childRelatedGroup.settings.parentProducts.errorLevel}
                            message={childRelatedGroup?.settings?.parentProducts?.message}
                          />
                        )}

                        {orderLinesByRelatedGroup[ key ].parentOrderLines.map((orderLine: IOrderLine) => (
                          <React.Fragment key={orderLine.id}>
                            {OrderProductLine(orderLine)}
                          </React.Fragment>
                        ))}
                      </div>

                    </RelatedGroupSection>
                  )}

                  {!!orderLinesByRelatedGroup[ key ].childOrderLines.length && (
                    <RelatedGroupSection
                      direction="vertical"
                    >
                      <div className="child-order-lines">
                        <Grid.Box className="related-group-header">
                          <Text color="white">
                            {productRelatedGroups.find(group => group.id === childRelatedGroupId)?.terminology.plural || "Related Product Group"}
                          </Text>

                          {parentRelatedGroup?.categoryId && (
                            <Link to={`/products?category=${parentRelatedGroup?.categoryId}`}>
                              {`View ${parentRelatedGroup?.terminology.plural}`}
                            </Link>
                          )}
                        </Grid.Box>

                        {parentRelatedGroup?.settings?.childProducts?.errorLevel && childTotal > parentTotal && (
                          <ErrorNotice
                            type={parentRelatedGroup.settings.childProducts.errorLevel}
                            message={parentRelatedGroup?.settings?.childProducts?.message}
                          />
                        )}

                        {orderLinesByRelatedGroup[ key ].childOrderLines.map((orderLine: IOrderLine, index) => (
                          <React.Fragment key={orderLine.id || `child-${orderLine.productId}-${index}`}>
                            {OrderProductLine(orderLine)}
                          </React.Fragment>
                        ))}

                      </div>
                    </RelatedGroupSection>
                  )}

                </div>
              );
            }

            return null;
          })}

        <div className="generic-order-lines">
          {!!genericOrderLines.length && genericOrderLines.map((orderLine, index) => (
            <React.Fragment key={orderLine.id || `generic-${orderLine.productId}-${index}`}>
              {OrderProductLine(orderLine)}
            </React.Fragment>
          ))}
        </div>
      </RelatedGroupsWrapper>
    );
  }

  return null;
};

const RelatedGroupSection = styled(Grid.Bounds)`
  ${({ theme }) => css`
    border: 1px solid ${theme.colors.primaryDark};
    padding: ${theme.spacing.padding.sm};
    border-top: none;

    &:first-child {
      margin-top: 0;
      border-top: 1px solid ${theme.colors.primaryDark};
    }

    .related-group-header {
      width: 100%;
      background-color: ${theme.colors.primaryDark};
      color: ${theme.colors.white};
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: ${theme.spacing.padding.sm};
      margin-bottom: ${theme.spacing.padding.sm};
      
      h3 {
        font-size: ${theme.fonts.sizes.p};
        margin-bottom: 0;
      }

      ${breakpoint("md", theme)} {
        h3 {
          font-size: ${theme.fonts.sizes.h3};
        }
      }

      a {
        color: ${theme.colors.white};
      }
    }
  `}
`;

const RelatedGroupsWrapper = styled.div`
  ${({ theme }) => css`
    ${breakpoint("md", theme)} {
      padding: ${theme.spacing.padding.md};
    }

    .generic-order-lines {
      margin-top: ${theme.spacing.margins.md};
    }
  `}
`;

export default RelatedProductSplit;
